import {
  BoundNumberField,
  BoundSelectField,
  Button,
  column,
  Css,
  getTableStyles,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  simpleHeader,
  SimpleHeaderAndData,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { UploaderProvider } from "src/contexts/UploaderContext";
import {
  AssetInput,
  IncrementalCollectionOp,
  Maybe,
  ReadyPlanAssetType,
  SaveProgramDataInput,
} from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import {
  PlanPackageElevationsV2_ReadyPlanOptionFragment,
  PlanPackageElevationsV2Query,
  useElevationCatalogV2Query,
  useSavePlanPackageElevationMutation,
} from "src/routes/libraries/plan-package-v2/components/ElevationsTab.generated";
import { PlanPackageAssetUploader } from "src/routes/libraries/plan-package/stepper/components/PlanPackageAssetUploader";

type AddEditElevationModalProps = {
  planPackage: PlanPackageElevationsV2Query["planPackage"];
  elevation?: PlanPackageElevationsV2_ReadyPlanOptionFragment;
};

export function AddEditElevationModal(props: AddEditElevationModalProps) {
  const { planPackage, elevation } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageElevationMutation();
  // The elevation catalog to pick from; also loaded when editing so the (disabled) field can show its current value
  const catalogQuery = useElevationCatalogV2Query();

  const formState = useFormState({
    config: elevationFormConfig,
    init: {
      input: elevation,
      map: (e) => ({ globalOptionId: e.globalOption.id, coverPhoto: maybeCoverPhoto(e), programData: e.programData }),
      ifUndefined: { globalOptionId: undefined, coverPhoto: undefined, programData: {} },
    },
  });

  async function onSave() {
    const saved = elevation ? await updateElevation(elevation) : await createElevation();
    triggerNotice({ message: `Elevation ${elevation ? "updated" : "added"}!` });
    closeModal();
    if (saved && saved.version.id !== planPackage.version.id) navigateToRpav(saved.version.id);
  }

  async function updateElevation(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
    const { coverPhoto } = formState.value;
    const pdChanges = formState.programData.changedValue;
    const record = ownProgramData(elevation);
    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          options: [
            {
              id: elevation.id,
              ...(formState.coverPhoto.dirty ? { assets: buildAssetsInput(elevation, coverPhoto) } : {}),
            },
          ],
          ...(Object.keys(pdChanges).nonEmpty
            ? {
                programDatas: [
                  {
                    op: IncrementalCollectionOp.Include,
                    // Sending both id and readyPlanOptionIds reads as a new, unversioned record
                    ...(record ? { id: record.id } : { readyPlanOptionIds: [elevation.id] }),
                    programData: pdChanges,
                  },
                ],
              }
            : {}),
        },
      },
    });
    return data?.savePlanPackage.planPackage;
  }

  async function createElevation() {
    const { globalOptionId, coverPhoto } = formState.value;
    const pdChanges = formState.programData.changedValue;
    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          // Elevations come from the global option catalog, so the code/name follow the `globalOptionId`
          options: [
            {
              active: true,
              globalOptionId,
              ...(coverPhoto ? { assets: buildAssetsInput(undefined, coverPhoto) } : {}),
            },
          ],
        },
      },
    });
    const saved = data?.savePlanPackage.planPackage;
    // Program data needs the RPO to exist first, so save it against the id we just got back
    const created = saved?.options.find((o) => o.globalOption.id === globalOptionId && o.active);
    if (created && Object.keys(pdChanges).nonEmpty) {
      // The new option already has a program data record; a second one is rejected as a duplicate
      const record = ownProgramData(created);
      await save({
        variables: {
          input: {
            id: planPackage.id,
            programDatas: [
              {
                op: IncrementalCollectionOp.Include,
                ...(record ? { id: record.id } : { readyPlanOptionIds: [created.id] }),
                programData: pdChanges,
              },
            ],
          },
        },
      });
    }
    return saved;
  }

  // Catalog elevations already on this plan package can't be added twice
  const usedGlobalOptionIds = new Set(
    planPackage.options
      .filter((o) => o.active && o.type.isElevation && o.id !== elevation?.id)
      .map((o) => o.globalOption.id),
  );

  const rows = useComputed(() => createRows(formState.programData), [formState]);

  return (
    <>
      <ModalHeader>{elevation ? "Edit Elevation" : "Add Elevation"}</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div css={Css.gray900.$}>
            Enter the values for this elevation (not deltas). Options will modify these values.
          </div>
          <BoundSelectField
            label="Elevation"
            field={formState.globalOptionId}
            options={catalogQuery.data?.globalOptions ?? []}
            getOptionLabel={(go) => `${go.code} - ${go.name}`}
            disabledOptions={[...usedGlobalOptionIds].map((id) => ({ value: id, reason: "Already in use" }))}
            disabled={!!elevation && "Cannot change the global option of an existing elevation."}
          />
          <GridTable id="elevationProgramData" columns={columns} rows={rows} style={tableStyle} />
          <div>
            <div css={Css.sm.gray700.mb1.$}>Cover Photo</div>
            <UploaderProvider>
              <Observer>
                {() => (
                  <PlanPackageAssetUploader
                    assetUrl={formState.coverPhoto.value?.previewUrl}
                    assetType={ReadyPlanAssetType.CoverPhoto}
                    saveAsset={(file) => formState.coverPhoto.set(toCoverPhoto(file))}
                  />
                )}
              </Observer>
            </UploaderProvider>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Observer>
          {() => <Button label="Save" disabled={!formState.valid && formState.errors.join(", ")} onClick={onSave} />}
        </Observer>
      </ModalFooter>
    </>
  );
}

type ProgramDataRow = {
  label: string;
  tooltip?: string;
  field?: FieldState<Maybe<number>>;
  /** Set when the server derives the value, so the row displays it instead of taking input. */
  readOnlyValue?: number;
  numFractionDigits?: number;
};

type Row = SimpleHeaderAndData<ProgramDataRow>;

const tableStyle = {
  // `flexible` rather than `fixed` so long labels can wrap instead of forcing a horizontal scroll
  ...getTableStyles({ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "flexible" }),
  // Keep the table within the modal instead of letting the label column push it into a horizontal scroll
  rootCss: Css.mw0.w100.$,
};

type CoverPhotoValue = Pick<AssetInput, "contentType" | "fileName" | "s3Key" | "sizeInBytes"> & {
  previewUrl?: Maybe<string>;
};

type ElevationProgramData = Pick<
  SaveProgramDataInput,
  | "netBuildableSqft"
  | "grossBuildableSqft"
  | "grossBelowGroundSqft"
  | "sellableAboveGroundSqft"
  | "sellableBelowGroundSqft"
  | "imperviousSqft"
  | "buildingHeightInFeet"
  | "widthInFeet"
  | "depthInFeet"
>;

type ElevationFormValue = {
  globalOptionId: Maybe<string>;
  coverPhoto: Maybe<CoverPhotoValue>;
  programData: ElevationProgramData;
};

const elevationFormConfig: ObjectConfig<ElevationFormValue> = {
  globalOptionId: { type: "value", rules: [required] },
  coverPhoto: { type: "value" },
  programData: {
    type: "object",
    config: {
      netBuildableSqft: { type: "value" },
      grossBuildableSqft: { type: "value" },
      grossBelowGroundSqft: { type: "value" },
      sellableAboveGroundSqft: { type: "value" },
      sellableBelowGroundSqft: { type: "value" },
      imperviousSqft: { type: "value" },
      buildingHeightInFeet: { type: "value" },
      widthInFeet: { type: "value" },
      depthInFeet: { type: "value" },
    },
  },
};

/** The record holding just this elevation's program data, as opposed to one shared with an option. */
function ownProgramData(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
  return elevation.programDatas.find((pd) => pd.readyPlanOptions.length === 1);
}

function maybeCoverPhoto(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment): Maybe<CoverPhotoValue> {
  const cover = elevation.assets.find((a) => a.active && a.isCoverPhoto);
  return cover && { previewUrl: cover.asset.previewUrl };
}

function toCoverPhoto(file: AssetUppyFile | undefined): Maybe<CoverPhotoValue> {
  return file
    ? {
        contentType: file.type,
        fileName: file.name,
        s3Key: file.meta.s3Key,
        sizeInBytes: file.data?.size ?? file.size ?? 0,
        previewUrl: file.meta.previewUrl,
      }
    : null;
}

function buildAssetsInput(
  elevation: Maybe<PlanPackageElevationsV2_ReadyPlanOptionFragment>,
  coverPhoto: Maybe<CoverPhotoValue>,
) {
  const kept = (elevation?.assets ?? []).filter((a) => !(a.active && a.isCoverPhoto)).map((a) => ({ id: a.id }));
  if (!coverPhoto) return kept;
  const { previewUrl, ...asset } = coverPhoto;
  return [...kept, { type: ReadyPlanAssetType.CoverPhoto, asset }];
}

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Elevation Details", w: 1, data: ({ label, tooltip }) => ({ content: label, tooltip }) }),
  column<Row>({
    header: "",
    data: ({ label, field, readOnlyValue, numFractionDigits }) =>
      field ? (
        <BoundNumberField label={label} field={field} numFractionDigits={numFractionDigits} />
      ) : (
        <span css={Css.gray700.w100.tar.$}>{readOnlyValue ?? "—"}</span>
      ),
    w: "120px",
  }),
];

function createRows(pd: ObjectState<ElevationFormValue>["programData"]): GridDataRow<Row>[] {
  const programDataDetails: ProgramDataRow[] = [
    {
      label: "Net Buildable Square Footage",
      field: pd.netBuildableSqft,
      tooltip: "Total square footage that can be built within the allowable building area.",
    },
    {
      label: "Gross Square Footage",
      field: pd.grossBuildableSqft,
      tooltip: "Total square footage of the building, including all above- and below-ground areas.",
    },
    { label: "Gross Below Ground Square Footage", field: pd.grossBelowGroundSqft },
    {
      label: "Sellable Square Footage",
      readOnlyValue: (pd.sellableAboveGroundSqft.value ?? 0) + (pd.sellableBelowGroundSqft.value ?? 0),
      tooltip:
        "Total square footage counted toward the home\u2019s sellable area. Calculated from Sellable Above Ground and Sellable Below Ground square footage.",
    },
    { label: "Sellable Above Ground Square Footage", field: pd.sellableAboveGroundSqft },
    { label: "Sellable Below Ground Square Footage", field: pd.sellableBelowGroundSqft },
    {
      label: "Unfinished Below Ground Square Footage",
      readOnlyValue: (pd.grossBelowGroundSqft.value ?? 0) - (pd.sellableBelowGroundSqft.value ?? 0),
      tooltip: "Below-ground square footage that is unfinished and not included in finished sellable area.",
    },
    {
      label: "Impervious SF",
      field: pd.imperviousSqft,
      tooltip:
        "Total area of impervious surfaces associated with the home, such as the building footprint and other non-permeable surfaces.",
    },
    { label: "Height", field: pd.buildingHeightInFeet, numFractionDigits: 2 },
    { label: "Width", field: pd.widthInFeet, numFractionDigits: 2 },
    { label: "Depth", field: pd.depthInFeet, numFractionDigits: 2 },
  ];
  return [simpleHeader, ...programDataDetails.map((d) => ({ id: d.label, kind: "data" as const, data: d }))];
}
